<template>
<div class="hris-page">
  <div class="hris-page-inner">
    <div class="hris-page-header">
      <span class="hris-page-eyebrow">System Settings</span>
      <h1 class="hris-page-title">Location Settings</h1>
      <p class="hris-page-subtitle">Manage geofenced locations used for attendance tracking.</p>
    </div>

    <q-form ref="vForm" @submit="onSubmit">
      <div class="row q-col-gutter-md">
        <div class="col-12 col-md-4">
          <div class="hris-form-section">
            <h3 class="hris-form-section-title">Add Location</h3>
            <div class="hris-form-field q-mb-md">
              <q-select v-model="province" option-value="id" option-label="province" :rules="[formRules.required]" :options="provinceOptions" label="Province" outlined dense color="green" emit-value map-options></q-select>
            </div>
            <div class="hris-form-field q-mb-md">
              <q-input v-model="location" label="Location" outlined dense color="green" :rules="[formRules.required]" class="caps"></q-input>
            </div>
            <div class="hris-form-field q-mb-md">
              <q-input v-model.number="radius" type="number" min="1" label="Radius (meters)" outlined dense color="green" :rules="[formRules.numberRequired]"></q-input>
            </div>
            <div class="hris-form-section-hint" style="margin-top: -6px;">{{ geocodeStatus }}</div>
            <div class="hris-form-actions" style="padding-top: 0;">
              <q-btn unelevated class="hris-q-primary full-width" type="submit" label="Save"></q-btn>
            </div>
          </div>
        </div>
        <div class="col-12 col-md-8">
          <div class="hris-toolbar">
            <div class="hris-toolbar-spacer"></div>
            <q-btn unelevated class="hris-q-outline" icon="fas fa-map-marker-alt" label="Backfill Missing Coordinates" @click="geocodeAllMissing"></q-btn>
          </div>
          <div class="hris-panel">
            <h3 class="hris-panel-title">Location List</h3>
            <q-table
              flat
              :data="data"
              :columns="columns"
              row-key="id"
              :pagination.sync="pagination"
            >
              <template v-slot:body="props">
                <q-tr :props="props" @click="selectedRow(props)" style="cursor: pointer;">
                  <q-td key="id" :props="props">{{ props.row.counter }}</q-td>
                  <q-td key="province" :props="props">{{ props.row.province }}</q-td>
                  <q-td key="location" :props="props">{{ props.row.location }}</q-td>
                  <q-td key="latitude" :props="props">{{ formatCoordinate(props.row.latitude) }}</q-td>
                  <q-td key="longitude" :props="props">{{ formatCoordinate(props.row.longitude) }}</q-td>
                  <q-td key="radius" :props="props">{{ props.row.radius || '-' }}</q-td>
                  <q-td key="has_coordinates" :props="props">
                    <span class="hris-badge" :class="props.row.has_coordinates ? 'hris-badge-success' : 'hris-badge-neutral'">
                      {{ props.row.has_coordinates ? 'Yes' : 'No' }}
                    </span>
                  </q-td>
                  <q-td key="action" :props="props">
                    <q-btn flat round dense class="hris-q-ghost" @click.stop="geocodeRow(props)">
                      <i class="fas fa-map-marker-alt"></i>
                      <q-tooltip>Retry Geocode</q-tooltip>
                    </q-btn>
                    <q-btn flat round dense class="hris-q-ghost" @click.stop="removeRow(props)">
                      <i class="fas fa-trash"></i>
                      <q-tooltip>Remove</q-tooltip>
                    </q-btn>
                  </q-td>
                </q-tr>
              </template>
              <template v-slot:no-data="">
                <div class="hris-empty full-width">No results found.</div>
              </template>
            </q-table>
          </div>
        </div>
      </div>
    </q-form>

    <q-dialog v-model="spinner" persistent>
      <div class="hris-loading" style="background: var(--hris-surface); border-radius: var(--hris-radius); padding: 32px 44px;">
        <q-spinner color="green" size="40px"></q-spinner>
      </div>
    </q-dialog>

    <q-dialog v-model="deleted" persistent>
      <div class="hris-modal">
        <div class="hris-modal-header">
          <h2 class="hris-modal-title">Confirm Deletion</h2>
        </div>
        <div class="hris-modal-body">
          This record will be permanently deleted.
        </div>
        <div class="hris-modal-footer">
          <q-btn flat label="Cancel" class="hris-q-ghost" v-close-popup></q-btn>
          <q-btn unelevated label="Continue" class="hris-q-primary" v-close-popup @click="deletionConfirmation"></q-btn>
        </div>
      </div>
    </q-dialog>
  </div>
</div>
</template>

<script>
import axios from 'axios';
import mixins from '../../mixins';


export default {

	data () {
		return {
			tempID:0,
			spinner:false,
			deleted:false,
			province:null,
			provinceOptions:[],
			location:null,
			radius:100,
			geocodeStatus:'Coordinates are auto-generated on save when geocoding is configured.',
			data:[],
			columns: [
			{ name: 'id', align: 'center', label: 'ID', field: 'id', sortable: true },
			{ name: 'province', align: 'center', label: 'Province', field: 'province', sortable: true },
			{ name: 'location', align: 'center', label: 'Location', field: 'location', sortable: true },
			{ name: 'latitude', align: 'center', label: 'Latitude', field: 'latitude', sortable: true },
			{ name: 'longitude', align: 'center', label: 'Longitude', field: 'longitude', sortable: true },
			{ name: 'radius', align: 'center', label: 'Radius (m)', field: 'radius', sortable: true },
			{ name: 'has_coordinates', align: 'center', label: 'Geocoded', field: 'has_coordinates', sortable: true },
			{ name: 'action', align: 'center', label: 'Action', field: '', sortable: true },
			],
			pagination: {
				rowsPerPage: 20
			},
		}
	},

	created(){
		this.displayProvinces()
		this.displayData()

	},

	methods: {

		onSubmit : function(){
			this.spinner = true;
			let formData = new FormData();
			formData.append("provinceID", this.province);
			formData.append("location", this.location);
			formData.append("radius", this.radius);
			formData.append("tempID", this.tempID);

			axios.create({
				baseURL : this.apiUrl,
				headers : {'Authorization' : `Bearer ${this.token}`}
			}).post('/admin/locations',formData)
			.then((res) => {
				this.displayData()
				this.province = ''
				this.location = ''
				this.radius = 100
				this.tempID = 0
				this.spinner = false;
				const geocoding = res.data.geocoding || {}
				this.geocodeStatus = geocoding.success
					? 'Saved and geocoded successfully.'
					: `Saved, but geocoding failed${geocoding.status ? ` (${geocoding.status})` : ''}.`
				this.$q.notify({ type: geocoding.success ? 'positive' : 'warning', message: this.geocodeStatus })
				this.$refs.vForm.reset()
			})
			.catch((error) => {
				this.spinner = false;
				const message = error?.response?.data?.message || 'Unable to save location.'
				this.$q.notify({ type: 'negative', message })
			});
		},

		displayData: function() {
			axios.create({
				baseURL: this.apiUrl,
				headers: {
					'Authorization': `Bearer ${this.token}`
				}})
			.get(`/admin/location`)
			.then((res) => {
				this.data = res.data.data;
				setTimeout(() => {
					this.spinner = false
				},500)
			})

		},


		displayProvinces: function() {
			axios.create({
				baseURL: this.apiUrl,
				headers: {
					'Authorization': `Bearer ${this.token}`
				}})
			.get(`/admin/provinces`)
			.then((res) => {

				this.provinceOptions = res.data.data;
				setTimeout(() => {
					this.spinner = false
				},500)
			})

		},

		selectedRow:function(obj){
			this.province = obj.row.provinceID
			this.location = obj.row.location
			this.radius = obj.row.radius || 100
			this.tempID = obj.row.id
		},

		removeRow:function(obj){

			this.deleted = true
			this.tempID = obj.row.id
		},

		deletionConfirmation:function(){
			this.spinner = true;
			let formData = new FormData();
			formData.append("tempID", this.tempID);
			axios.create({
				baseURL : this.apiUrl,
				headers : {'Authorization' : `Bearer ${this.token}`}
			}).post('/admin/location/delete',formData)
			.then((res) => {
				this.tempID = 0
				this.displayData()
			});
		},

		geocodeRow: function(obj){
			this.spinner = true
			axios.create({
				baseURL : this.apiUrl,
				headers : {'Authorization' : `Bearer ${this.token}`}
			}).post(`/admin/location/geocode/${obj.row.id}`)
			.then((res) => {
				this.spinner = false
				const geocoding = res.data.geocoding || {}
				const message = geocoding.success
					? 'Location geocoded successfully.'
					: `Unable to geocode location${geocoding.status ? ` (${geocoding.status})` : ''}.`
				this.$q.notify({ type: geocoding.success ? 'positive' : 'warning', message })
				this.displayData()
			})
			.catch((error) => {
				this.spinner = false
				const message = error?.response?.data?.message || 'Unable to geocode location.'
				this.$q.notify({ type: 'negative', message })
			})
		},

		geocodeAllMissing: function(){
			this.spinner = true
			let formData = new FormData()
			formData.append("limit", 100)
			axios.create({
				baseURL : this.apiUrl,
				headers : {'Authorization' : `Bearer ${this.token}`}
			}).post('/admin/location/geocode-all', formData)
			.then((res) => {
				this.spinner = false
				const data = res.data.data || {}
				const message = `Geocode backfill completed. Updated: ${data.updated || 0}, Failed: ${data.failed || 0}.`
				this.$q.notify({ type: 'info', message })
				this.displayData()
			})
			.catch((error) => {
				this.spinner = false
				const message = error?.response?.data?.message || 'Unable to backfill coordinates.'
				this.$q.notify({ type: 'negative', message })
			})
		},

		formatCoordinate: function(value){
			if (value === null || value === undefined || value === '') {
				return '-'
			}

			const parsed = Number(value)
			if (Number.isNaN(parsed)) {
				return '-'
			}

			return parsed.toFixed(6)
		}

	}
}
</script>
<style>
.caps {
	text-transform: uppercase
}
</style>
